import { Button } from "@tql/ui-components-react/tql/buttons";
import { upload_01Line } from "@tql/ui-components-react/untitled/icons";
import type { MouseEvent } from "react";
import { useCallback, useContext, useState } from "react";

import { QuoteDetailsContext } from "~/features/quote/context/quote-details-context.tsx";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useFeatureFlagGate } from "~/shared/hooks/use-feature-flag-gate.ts";

import { BulkUploadModal } from "./index.tsx";

type FeatureGate = { isReady: boolean; isEnabled: boolean };

const isFeatureAvailable = ({ isReady, isEnabled }: FeatureGate): boolean =>
	isReady && isEnabled;

const canShowBulkUpload = (
	isInternal: boolean,
	bulkUploadGate: FeatureGate,
	bulkTenderGate: FeatureGate,
	externalBulkQuoteGate: FeatureGate,
): boolean =>
	isFeatureAvailable(bulkUploadGate) &&
	(isInternal ||
		isFeatureAvailable(bulkTenderGate) ||
		isFeatureAvailable(externalBulkQuoteGate));

export const BulkUploadButton = () => {
	const bulkUploadGate = useFeatureFlagGate("traxLtlBulkUpload");
	const bulkTenderGate = useFeatureFlagGate("traxLtlBulkTender");
	const externalBulkQuoteGate = useFeatureFlagGate("traxLtlExternalBulkQuote");
	const { isInternal } = useDeploymentType();
	const { quoteState } = useContext(QuoteDetailsContext);
	const [showModal, setShowModal] = useState(false);

	const toggleModal = useCallback(() => {
		setShowModal((prev) => !prev);
	}, []);

	const handleClick = useCallback(
		(event: MouseEvent<HTMLButtonElement>) => {
			// Prevent the click from bubbling up to the accordion header which
			// would otherwise toggle the panel when this button is rendered as
			// the panel's header content.
			event.stopPropagation();
			toggleModal();
		},
		[toggleModal],
	);

	if (
		!canShowBulkUpload(
			isInternal,
			bulkUploadGate,
			bulkTenderGate,
			externalBulkQuoteGate,
		)
	) {
		return null;
	}

	const isDisabled = !quoteState.customer?.id;

	return (
		<>
			<div className={"bulk-upload-button"}>
				<Button
					disabled={isDisabled}
					onClick={handleClick}
					svgIcon={upload_01Line}
					tabIndex={-1}
				>
					Bulk Upload
				</Button>
			</div>
			{showModal ? (
				<BulkUploadModal toggleBulkUploadModal={toggleModal} />
			) : null}
		</>
	);
};
